import * as React from 'react'
import { Fragment } from 'react'
import { ENTITY_TYPES, updateLoadingState } from '@sales-center/common'
import { useGetDetailOnMount } from '@user/features/EntityDetails/hooks'
import { SettingsPage } from '@sales-center/components'
import { useData } from './hooks'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import { LeadDetailPageContent } from '@user/components/Leads/DetailPage/PageContent'
import { OpportunityDetailPageContent } from '@user/components/Opportunities/DetailPage/PageContent'
import type { ILeadDetail } from '@user/store/lead'
import type { IOpportunityDetail } from '@user/store/opportunity'
import { ActivitiesContextProvider } from '@user/features/Activity/context'
import {
  useGetAllFunnelStagesOnMount,
  useGetSalesFunnelListOnMount,
} from '@user/store/salesFunnel'
import { useGetAttachmentsOnMount } from '@user/store/attachments'
import { useGetUserListOnMount } from '@user/store/users'
import { useAppDispatch } from '@user/store/hooks'

type Props = {
  entityType: ENTITY_TYPES
  canConvertEntity?: boolean
  canDeleteEntity?: boolean
  canUpdateEntity?: boolean
}

export function EntityDetails(props: Props) {
  const {
    entityType,
    canDeleteEntity = false,
    canUpdateEntity = false,
    canConvertEntity = false,
  } = props
  const { title, details, isLeadDetails } = useData(entityType)

  const status = useGetDetailOnMount(entityType)
  useGetAllFunnelStagesOnMount()
  const funnelStageStatus = useGetAllFunnelStagesOnMount()
  const funnelListStatus = useGetSalesFunnelListOnMount()
  const userListStatus = useGetUserListOnMount()
  const dispatch = useAppDispatch()

  useGetAttachmentsOnMount(details?.id || -1, entityType === ENTITY_TYPES.Lead)

  React.useEffect(() => {
    if (status === 'success') dispatch(updateLoadingState('hidden'))
  }, [dispatch, status])

  return (
    <SettingsPage title={title} isDetailPage padding="0px">
      {(status == 'pending' ||
        funnelStageStatus === 'pending' ||
        userListStatus === 'pending' ||
        funnelListStatus === 'pending') && (
        <Box p={2} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}
      <ActivitiesContextProvider>
        <Fragment>
          {status == 'success' && isLeadDetails && (
            <LeadDetailPageContent
              detail={details as ILeadDetail}
              canConvertLeads={canConvertEntity}
              canDeleteLeads={canDeleteEntity}
              canUpdateLeads={canUpdateEntity}
            />
          )}
          {status == 'success' && !isLeadDetails && (
            <OpportunityDetailPageContent
              detail={details as IOpportunityDetail}
              canDeleteOpps={canDeleteEntity}
              canUpdateOpps={canUpdateEntity}
            />
          )}
        </Fragment>
      </ActivitiesContextProvider>
      {status == 'fail' && (
        <Box p={2} display="flex" justifyContent="center">
          <Alert severity="error">Failed to load lead details.</Alert>
        </Box>
      )}
    </SettingsPage>
  )
}
